import React, { useState } from 'react';
import { useCluster } from '../context/ClusterContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CreateNamespaceModal from '../components/CreateNamespaceModal';
import WebTerminal from '../components/WebTerminal';
import './ResourcePage.css';

function Namespaces() {
  const { selectedCluster, resources, resourcesLoading, resourcesError, refreshResources } = useCluster();
  const navigate = useNavigate();
  const { hasSectionAction } = useAuth();
  // Namespaces live under the sidebar's "Cluster" section (same as the shared
  // resources view endpoint), so creation is gated by 'clusters', not 'workloads'.
  const canCreate = hasSectionAction('clusters', 'create');
  const [filter, setFilter]           = useState('');
  const [showCreate, setShowCreate]   = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);

  if (!selectedCluster) {
    return (
      <div className="resource-page">
        <div className="page-header"><h1>Namespaces</h1></div>
        <div className="no-cluster-msg">
          No cluster selected. <span className="link" onClick={() => navigate('/clusters')}>Add or select a cluster</span> to view namespaces.
        </div>
      </div>
    );
  }

  if (!selectedCluster.is_imported) {
    return (
      <div className="resource-page">
        <div className="page-header"><h1>Namespaces — {selectedCluster.name}</h1></div>
        <div className="no-cluster-msg">
          Live resource viewing is available for imported clusters only.
        </div>
      </div>
    );
  }

  const namespaces = resources?.namespaces || [];
  const filtered = namespaces.filter(n => n.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="resource-page">
      <div className="page-header">
        <div>
          <h1>Namespaces</h1>
          <div className="resource-cluster-badge">{selectedCluster.name}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" style={{ fontFamily: 'monospace' }}
            onClick={() => setShowTerminal(true)}>⌨ Terminal</button>
          {canCreate && (
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Create Namespace</button>
          )}
          <button className="btn btn-secondary" onClick={refreshResources}>Refresh</button>
        </div>
      </div>

      {resourcesError && <div className="resource-error">{resourcesError}</div>}

      {resourcesLoading ? (
        <div className="resource-loading">Loading namespaces...</div>
      ) : (
        <>
          <div className="resource-filter">
            <input
              type="text"
              placeholder="Filter namespaces..."
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
            <span className="resource-count">{filtered.length} / {namespaces.length} namespaces</span>
          </div>
          <div className="table-card">
            <table className="ds">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No namespaces found</td></tr>
                ) : filtered.map(ns => (
                  <tr key={ns.name}>
                    <td><code>{ns.name}</code></td>
                    <td>
                      <span className={`status-badge ${ns.status === 'Active' ? 'status-active' : 'status-failed'}`}>
                        {ns.status}
                      </span>
                    </td>
                    <td style={{ color: 'var(--muted)', fontSize: 13 }}>
                      {ns.created_at ? new Date(ns.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showCreate && (
        <CreateNamespaceModal
          clusterId={selectedCluster.id}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refreshResources(); }}
        />
      )}

      {showTerminal && (
        <WebTerminal
          clusterId={selectedCluster.id}
          onClose={() => setShowTerminal(false)}
        />
      )}
    </div>
  );
}

export default Namespaces;
